import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import Contents from './components/Contents';
import BrochureForm from './components/BrochureForm';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';
import Seo from '@/components/seo/Seo';

function BackToTop() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 w-10 h-10 flex items-center justify-center rounded-full border border-accent-400/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 z-50 cursor-pointer"
      aria-label={t('corporateBrochure.page.backToTop')}
    >
      <i className="ri-arrow-up-line text-base"></i>
    </button>
  );
}

export default function CorporateBrochurePage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();

  const serviceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: t('corporateBrochure.jsonld.serviceName'),
    provider: {
      '@type': 'Organization',
      name: t('corporateBrochure.jsonld.orgName'),
    },
    serviceType: t('corporateBrochure.jsonld.serviceType'),
    areaServed: {
      '@type': 'Country',
      name: lang === 'en' ? 'Japan' : t('corporateBrochure.jsonld.areaServed'),
    },
    description: t('corporateBrochure.jsonld.description'),
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('corporateBrochure.jsonld.breadcrumb1'), item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: t('corporateBrochure.jsonld.breadcrumb2'), item: 'https://butler-academy.jp/corporate' },
      { '@type': 'ListItem', position: 3, name: t('corporateBrochure.jsonld.breadcrumb3'), item: 'https://butler-academy.jp/corporate/brochure' },
    ],
  };

  return (
    <>
      <Seo route="/corporate/brochure" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />

      <Header />
      <main>
        <HeroSection />
        <Contents />
        <BrochureForm />
        <CTASection />
        <FAQSection />
        <RelatedPages />

        <footer className="py-12 bg-background-100/50 text-center">
          {String(t('corporateBrochure.page.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-400 tracking-[0.04em]">{__s}</p>))}
        </footer>

        <BackToTop />
      </main>
      <Footer />
    </>
  );
}
